import {
  generateBindings,
  observableValue,
  useEffect,
  useRef,
  useSharedState,
  view,
  WritableObservableValue,
} from '@esui/core';
import { ParserFormatter } from '@esui/core/formatters';
import { spread } from '@esui/core/utils/spread.js';
import '@material/mwc-textfield';
import { TextField, TextFieldType } from '@material/mwc-textfield';
import { css, html } from 'lit';
import { ref } from 'lit/directives/ref.js';

export const [Textfield] = view('esui-mwc-textfield', {
  Props: class {
    label?: string = undefined;
    name?: string = undefined;
    type: TextFieldType = 'text';
    value: WritableObservableValue<any> = observableValue('');
    formatter: ParserFormatter<unknown> = ParserFormatter.default;
    isValid = observableValue(false);
    placeholder?: string = undefined;
    prefix?: string = undefined;
    suffix?: string = undefined;
    icon?: string = undefined;
    iconTrailing?: string = undefined;
    disabled = false;
    required = false;
    charCounter = false;
    outlined = false;
    helper?: string = undefined;
    helperPersistent = false;
    maxLength?: number = undefined;
    minLength?: number = undefined;
    validationMessage?: string = undefined;
    pattern?: string = undefined;
    min?: number | string = undefined;
    max?: number | string = undefined;
    size: number | null = null;
    step: number | null = null;
    autoValidate = false;
    validityTransform:
      | ((
          value: string,
          nativeValidity: ValidityState
        ) => Partial<ValidityState>)
      | null = null;
  },

  mapBuilder:
    TextfieldBuilder =>
    <T>(label: string, value: WritableObservableValue<T>) =>
      TextfieldBuilder().label(label).value(value),

  template(props) {
    const content = useSharedState(props.value);
    const isValid = useSharedState(props.isValid);

    const inputRef = useRef<MdcTextfield>();

    const value = props.formatter.format(content.value);
    const handleInput = (e: { target: HTMLInputElement }) => {
      if (e.target.checkValidity()) {
        content.value = props.formatter.parse(e.target.value);
      }
    };

    const computedProps = generateBindings(props, [
      'value',
      'formatter',
      'isValid',
      'validityTransform',
    ]);

    const handleChange = (e: { target: HTMLInputElement }) => {
      isValid.value = e.target.checkValidity();
    };

    useEffect(
      element => {
        // eslint-disable-next-line no-param-reassign
        element.focus = () => {
          inputRef.value?.focus();
        };
      },
      [inputRef.value]
    );

    return html`
      <mwc-textfield
        ${ref(inputRef)}
        .value="${value}"
        ${spread({ ...computedProps })}
        .validityTransform="${props.validityTransform}"
        @change="${handleChange}"
        @input="${handleInput}"
      ></mwc-textfield>
    `;
  },
  cssTemplate: css`
    mwc-textfield {
      display: inline-block;
      width: 100%;
    }
  `,
});

export type MdcTextfield = TextField;
